import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, FileText, Inbox } from "lucide-react";
import { getCoas } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";

export default function Coas() {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["coas", q], queryFn: () => getCoas(q) });
  const coas = data?.coas || [];

  useSEO({
    title: "COAs / Testing & Documentation | Renova Peptide Co.",
    description:
      "Search and browse Certificates of Analysis and supporting documentation for Renova Peptide Co. research materials by product name, batch, or lot.",
    path: "/coas",
    jsonLd: breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "COAs / Testing", path: "/coas" },
    ]),
  });

  return (
    <>
      <PageHeader
        overline="Documentation"
        title="COAs / Testing"
        intro="Search and browse available Certificates of Analysis and supporting documents. Genuine documents are published here as they are supplied for each material."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "COAs / Testing", path: "/coas" }]}
      />

      <section className="container-rnv py-16 md:py-20">
        <Reveal>
          <div className="relative max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#85827D]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by product name, batch, or lot number…"
              aria-label="Search documentation"
              className="w-full bg-white border border-[#1E2A44]/12 rounded-sm pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-[#C8A96A]"
              data-testid="coa-search"
            />
          </div>
        </Reveal>

        <div className="mt-10">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs uppercase tracking-[0.14em] text-[#85827D] border-b border-[#1E2A44]/12">
            <span className="col-span-5">Product</span>
            <span className="col-span-3">Batch / Lot</span>
            <span className="col-span-2">Date</span>
            <span className="col-span-2 text-right">Document</span>
          </div>

          {coas.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#1E2A44]/15 rounded-sm mt-4" data-testid="coa-empty">
              <Inbox size={40} className="mx-auto text-[#C8A96A]" strokeWidth={1.4} />
              <p className="font-serif text-2xl text-[#1E2A44] mt-5">No documents available yet</p>
              <p className="mt-2 text-sm text-[#85827D] max-w-md mx-auto">
                Certificates of Analysis will appear here as genuine documents are supplied
                for each product lot. We do not publish fabricated documentation.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#1E2A44]/10" data-testid="coa-list">
              {coas.map((c) => (
                <li key={c.id} className="grid grid-cols-2 md:grid-cols-12 gap-4 items-center px-5 py-5">
                  <span className="md:col-span-5 font-medium text-[#1E2A44]">{c.product}</span>
                  <span className="md:col-span-3 text-sm text-[#85827D]">{c.batch}</span>
                  <span className="md:col-span-2 text-sm text-[#85827D]">{c.date}</span>
                  <a href={c.url} className="md:col-span-2 md:text-right text-sm text-[#1E2A44] inline-flex items-center gap-1.5 md:justify-end">
                    <FileText size={15} className="text-[#C8A96A]" /> View
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
